import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ICrmError, JourneyKey } from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type PipelineOwner = 'all' | 'me' | 'unassigned';
/** How cards are ordered in each column. */
export type PipelineSort = 'priority' | 'paid' | 'funded' | 'recent';

export interface IPipelineCard {
    id: number;
    name: string;
    company: string | null;
    email: string;
    owner_user_id: number | null;
    owner_name: string | null;
    score: number;
    last_activity_at: string | null;
    created_at: string | null;
    awaiting_reply: boolean;
    funded: boolean;
    ordered: boolean;
    /** Dollars paid to Wallmates (orders and mockup purchases). */
    paid?: number;
    /** Dollars of credit loaded (top-ups), and what's left. */
    funded_amount?: number;
    credit_balance?: number;
}

export interface IPipelineColumn {
    key: string;
    label: string;
    hint: string | null;
    phase: 'prospect' | 'customer';
    won: boolean;
    past_won: boolean;
    count: number;
    /** Over the whole column, not only the cards shown. */
    paid_total?: number;
    funded_total?: number;
    cards: IPipelineCard[];
}

export interface IPipelineResult {
    journeys: { key: JourneyKey; label: string; count: number }[];
    board: {
        journey: { key: JourneyKey; label: string };
        columns: IPipelineColumn[];
        totals: { people: number; prospects: number; customers: number; won: number; new_this_week: number; awaiting_reply: number; paid?: number; funded?: number };
    };
}

// #region useCrmPipeline
/** The pipeline board read. Resolves the payload or a typed ICrmError; never throws. A 401 logs out. */
export const useCrmPipeline = (): [(journey: JourneyKey, owner: PipelineOwner, q: string, sort?: PipelineSort) => Promise<IPipelineResult | ICrmError>] => {
    const [, authToken, loginFunctions] = useLogin();

    const getPipeline = async (journey: JourneyKey, owner: PipelineOwner, q: string, sort: PipelineSort = 'priority'): Promise<IPipelineResult | ICrmError> => {
        try {
            const result: AxiosResponse<IPipelineResult> = await axios.get(`${crmUrl}/pipeline`, {
                headers: { 'X-Wallmates-Auth': authToken },
                params: { journey, owner, sort, ...(q.trim() ? { q: q.trim() } : {}) }
            });
            return result.data;
        } catch (err: any) {
            const status = err?.response?.status ?? 0;
            if (status === 401) loginFunctions.logout();
            return { ok: false, status, message: err?.response?.data?.message || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.') };
        }
    };

    return [getPipeline];
};
